import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, Check, ChevronLeft, ChevronRight, Download, FileText, RefreshCw, X } from 'lucide-react';
import TopBar from '../components/TopBar';
import Loader from '../components/Loader';
import { api, errorMessage } from '../lib/api';
import { cn, qty } from '../lib/utils';

/* Every receipt confirmed from Inny, with its PDF. Filter by PO, supplier,
   person, status and dates; page through; tick receipts or export all that
   match as one ZIP (a PDF each plus a receipts.csv index). */
const STATUS = {
  validated: { word: 'Validated', tone: 'text-out', icon: Check },
  partial: { word: 'Partly validated', tone: 'text-hold', icon: AlertTriangle },
  failed: { word: 'Not validated', tone: 'text-alert', icon: AlertTriangle },
};
const EMPTY = { po: '', supplier: '', user: '', status: '', from: '', to: '' };

const dayKey = (iso) => new Date(iso).toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
const timeOf = (iso) => new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

function useDebounced(value, ms = 350) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

function saveBlob(blob, name) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}

export default function Receipts() {
  const navigate = useNavigate();
  const [filters, setFilters] = useState(EMPTY);
  const [more, setMore] = useState(false); // person, status and dates: open on request
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [exporting, setExporting] = useState(null); // text while a ZIP is being prepared
  const [exportError, setExportError] = useState(null);

  const text = useDebounced(`${filters.po}\u0000${filters.supplier}`);
  const query = useMemo(() => {
    const [po, supplier] = text.split('\u0000');
    return { po, supplier, user: filters.user, status: filters.status, from: filters.from, to: filters.to };
  }, [text, filters.user, filters.status, filters.from, filters.to]);
  const active = Object.values(filters).some(Boolean);

  const latest = useRef(0); // a slow answer to an older filter must not replace a newer one
  const load = useCallback(async () => {
    const mine = ++latest.current;
    setBusy(true);
    setError(null);
    try {
      const params = Object.fromEntries(Object.entries({ ...query, page, page_size: 25 }).filter(([, v]) => v !== '' && v != null));
      const got = await api.receipts(params);
      if (mine === latest.current) setData(got);
    } catch (err) {
      if (mine === latest.current) setError(errorMessage(err));
    } finally {
      if (mine === latest.current) setBusy(false);
    }
  }, [query, page]);

  useEffect(() => {
    load();
  }, [load]);

  const set = (key) => (e) => {
    setPage(1); // new filters start on page 1
    setFilters((f) => ({ ...f, [key]: e.target.value }));
  };
  const toggle = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const rows = data?.receipts || [];
  const allOnPage = rows.length > 0 && rows.every((r) => selected.has(r.id));
  const togglePage = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      rows.forEach((r) => (allOnPage ? next.delete(r.id) : next.add(r.id)));
      return next;
    });

  const runExport = async (body, label) => {
    setExporting(label);
    setExportError(null);
    try {
      const { blob, filename } = await api.exportReceipts(body);
      saveBlob(blob, filename || 'inny-receipts.zip');
    } catch (err) {
      setExportError(await api.blobErrorMessage(err));
    } finally {
      setExporting(null);
    }
  };

  const days = [];
  rows.forEach((r) => {
    const key = dayKey(r.created_at);
    const last = days[days.length - 1];
    if (last && last.key === key) last.items.push(r);
    else days.push({ key, items: [r] });
  });
  const total = data?.total ?? 0;
  const tooMany = data && total > data.export_max;
  const field = 'h-11 w-full rounded-xl border border-line bg-surface px-3 text-[15px] outline-none focus:border-ink';

  return (
    <div className="min-h-screen pb-28">
      <TopBar
        title="Receipts"
        subtitle={data ? `${total} ${total === 1 ? 'receipt' : 'receipts'}${active ? ' match' : ' confirmed from Inny'}` : 'What was confirmed from Inny, with its report'}
        onBack={() => navigate('/')}
        right={
          <button type="button" onClick={load} disabled={busy} aria-label="Refresh" className="flex h-11 w-11 items-center justify-center rounded-full active:bg-sunken">
            <RefreshCw className={cn('h-5 w-5', busy && 'animate-spin')} />
          </button>
        }
      />
      <main className="mx-auto max-w-lg px-3">
        {/* filters */}
        <section className="mt-3 rounded-xl bg-surface p-3">
          <div className="grid grid-cols-2 gap-2">
            <input value={filters.po} onChange={set('po')} placeholder="PO number" inputMode="search" autoComplete="off" className={field} />
            <input value={filters.supplier} onChange={set('supplier')} placeholder="Supplier" inputMode="search" autoComplete="off" className={field} />
            {(more || filters.user || filters.status || filters.from || filters.to) && (<>
            <select value={filters.user} onChange={set('user')} className={field} aria-label="Received by">
              <option value="">Anyone</option>
              {(data?.people || []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name} ({p.receipts})
                </option>
              ))}
            </select>
            <select value={filters.status} onChange={set('status')} className={field} aria-label="Status">
              <option value="">Any status</option>
              <option value="validated">Validated</option>
              <option value="partial">Partly validated</option>
              <option value="failed">Not validated</option>
            </select>
            <label className="block">
              <span className="text-[11px] font-semibold text-ink2">From</span>
              <input type="date" value={filters.from} onChange={set('from')} className={field} />
            </label>
            <label className="block">
              <span className="text-[11px] font-semibold text-ink2">To</span>
              <input type="date" value={filters.to} onChange={set('to')} className={field} />
            </label>
            </>)}
          </div>
          <div className="mt-2 flex flex-wrap items-center justify-between gap-2">
            {active ? (
              <button type="button" onClick={() => { setPage(1); setFilters(EMPTY); }} className="inline-flex items-center gap-1 text-[13px] font-semibold text-ink2">
                <X className="h-4 w-4" /> Clear filters
              </button>
            ) : (
              <button type="button" onClick={() => setMore((v) => !v)} className="h-10 text-[13px] font-semibold text-ink2">{more ? 'Fewer filters' : 'More filters'}</button>
            )}
            {!tooMany && <button
              type="button"
              onClick={() => runExport({ filters: query }, `Preparing ${total} ${total === 1 ? 'report' : 'reports'}…`)}
              disabled={!total || tooMany || exporting !== null}
              className="inline-flex h-10 items-center gap-1.5 rounded-xl bg-ink px-3 text-[13px] font-semibold text-bg active:opacity-80 disabled:opacity-40"
            >
              <Download className="h-4 w-4" /> Export {active ? 'these' : 'all'} {total} as ZIP
            </button>}
          </div>
          {tooMany && <p className="mt-1 text-[12px] text-ink2">To export, filter to {data.export_max} reports or fewer, for example one month.</p>}
        </section>

        {exporting && <p className="mt-3 rounded-xl bg-sunken px-3 py-2 text-[13px] text-ink2">{exporting} About ten seconds per hundred reports; keep this screen open.</p>}
        {exportError && <p className="mt-3 rounded-xl bg-alert-bg px-3 py-2 text-[14px] text-alert">{exportError}</p>}
        {error && <p className="mt-3 rounded-xl bg-alert-bg px-3 py-2 text-[14px] text-alert">{error}</p>}
        {!data && !error && <Loader label="Fetching receipts…" />}
        {data && rows.length === 0 && <p className="mt-10 text-center text-[15px] text-ink2">{active ? 'No receipt matches these filters.' : 'Nothing confirmed yet.'}</p>}

        {rows.length > 0 && (
          <div className="mt-3 flex items-center justify-between px-1">
            <button type="button" onClick={togglePage} className="inline-flex h-10 items-center gap-2 text-[13px] font-semibold text-ink2">
              <span className={cn('flex h-5 w-5 items-center justify-center rounded-md border-2', allOnPage ? 'border-ink bg-ink text-bg' : 'border-line')}>{allOnPage && <Check className="h-3.5 w-3.5" strokeWidth={3} />}</span>
              {allOnPage ? 'Untick this page' : 'Tick this page'}
            </button>
            <span className="text-[12px] text-ink2">
              {(data.page - 1) * data.page_size + 1}–{(data.page - 1) * data.page_size + rows.length} of {total}
            </span>
          </div>
        )}

        {days.map((d) => (
          <section key={d.key} className="mt-3">
            <h2 className="mb-1.5 px-1 text-[13px] font-semibold text-ink2">{d.key}</h2>
            <ul className="space-y-1.5">
              {d.items.map((r) => {
                const s = STATUS[r.status] || STATUS.validated;
                const Icon = s.icon;
                const on = selected.has(r.id);
                return (
                  <li key={r.id} className={cn('flex items-center gap-2 rounded-xl bg-surface py-2 pl-2 pr-2', on && 'ring-2 ring-ink/70')}>
                    <button type="button" onClick={() => toggle(r.id)} aria-pressed={on} aria-label={`Select ${r.po_name}`} className="flex h-11 w-9 shrink-0 items-center justify-center">
                      <span className={cn('flex h-6 w-6 items-center justify-center rounded-md border-2', on ? 'border-ink bg-ink text-bg' : 'border-line')}>{on && <Check className="h-4 w-4" strokeWidth={3} />}</span>
                    </button>
                    <span className="min-w-0 flex-1">
                      <span className="flex items-baseline gap-2">
                        <span className="font-cond shrink-0 text-[19px] font-bold leading-none">{r.po_name}</span>
                        <span className="min-w-0 truncate text-[13px] text-ink2">{r.supplier}</span>
                      </span>
                      <span className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[12px] text-ink2">
                        <span>{timeOf(r.created_at)}</span>
                        {r.user && <span>{r.user}</span>}
                        <span>
                          {qty(r.units)} {r.units === 1 ? 'unit' : 'units'} on {r.lines} {r.lines === 1 ? 'line' : 'lines'}
                        </span>
                        {r.pickings?.length > 0 && <span className="font-mono">{r.pickings.join(', ')}</span>}
                        <span className={cn('inline-flex items-center gap-1 font-semibold', s.tone)}>
                          <Icon className="h-3.5 w-3.5" /> {s.word}
                        </span>
                      </span>
                    </span>
                    <a href={r.report_url} target="_blank" rel="noreferrer" className="inline-flex h-11 shrink-0 items-center gap-1.5 rounded-xl bg-sunken px-3 text-[13px] font-semibold active:bg-line" aria-label={`Report for ${r.po_name}`}>
                      <FileText className="h-4 w-4" /> PDF
                    </a>
                  </li>
                );
              })}
            </ul>
          </section>
        ))}

        {data && data.pages > 1 && (
          <nav className="mt-4 flex items-center justify-between gap-2" aria-label="Pages">
            <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={data.page <= 1 || busy} className="inline-flex h-11 items-center gap-1 rounded-xl bg-surface px-3 text-[14px] font-semibold active:bg-sunken disabled:opacity-40">
              <ChevronLeft className="h-4 w-4" /> Newer
            </button>
            <span className="text-[13px] text-ink2">
              Page {data.page} of {data.pages}
            </span>
            <button type="button" onClick={() => setPage((p) => Math.min(data.pages, p + 1))} disabled={data.page >= data.pages || busy} className="inline-flex h-11 items-center gap-1 rounded-xl bg-surface px-3 text-[14px] font-semibold active:bg-sunken disabled:opacity-40">
              Older <ChevronRight className="h-4 w-4" />
            </button>
          </nav>
        )}
      </main>

      {selected.size > 0 && (
        <div className="safe-b fixed inset-x-0 bottom-0 z-20 bg-bg/95 backdrop-blur">
          <div className="mx-auto flex max-w-lg items-center gap-2 px-3 py-2">
            <button type="button" onClick={() => setSelected(new Set())} aria-label="Clear the selection" className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-surface active:bg-sunken">
              <X className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={() => runExport({ ids: [...selected] }, `Preparing ${selected.size} ${selected.size === 1 ? 'report' : 'reports'}…`)}
              disabled={exporting !== null}
              className="flex h-12 flex-1 items-center justify-center gap-2 rounded-xl bg-ink text-[15px] font-semibold text-bg active:opacity-80 disabled:opacity-50"
            >
              <Download className="h-5 w-5" /> {exporting ? 'Preparing…' : `Download ${selected.size} as ZIP`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
